import { useCurrentAgreement } from '@economic/react-app';
import { Alert, AlertDialog, Button, Dialog, Group, Icon } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useQuery from '~/app/shared/hooks/useQuery';
import date from '~/app/shared/util/utilities/date';
import { LoadingBox } from '~/shared-ui';
import { SkatVatStatementSubmisssionModel } from '../../../../../../types/internal-api';
import { getErrorMessage } from '../../../../../../utilities/parseAPIValidationErrors';
import ExternalLink from '../../../../../shared/components/ExternalLink';
import { Form } from '../../../../../shared/components/Form';
import { useLocalization } from '../../../../../shared/components/l10n/l10n';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import PaperPlaneWithDollarSign from './paper-plane-with-dollar-sign.svg';
import { TransferToSkatDialogState } from './types';

interface TransferToSkatErrorDialogProps {
    errorMessage;
    onClose: () => void;
}

function TransferToSkatErrorDialog({ errorMessage, onClose }: TransferToSkatErrorDialogProps) {
    return (
        <AlertDialog defaultOpen>
            <AlertDialog.Content aria-label={t`Transfer failed`}>
                <AlertDialog.Title>
                    <Trans>Transfer failed</Trans>
                </AlertDialog.Title>
                <p>{errorMessage}</p>
                <AlertDialog.Footer>
                    <AlertDialog.Action>
                        <Button appearance="primary" onClick={onClose} autoFocus>
                            <Trans>OK</Trans>
                        </Button>
                    </AlertDialog.Action>
                </AlertDialog.Footer>
            </AlertDialog.Content>
        </AlertDialog>
    );
}

interface TransferToSkatDialogContentProps {
    loading: boolean;
    existingSubmission?: SkatVatStatementSubmisssionModel;
    errorMessage;
    onTransfer: () => void;
    onError: (error) => void;
    onClose: () => void;
}

function TransferToSkatDialogContent({
    loading,
    existingSubmission,
    errorMessage,
    onTransfer,
    onError,
    onClose,
}: TransferToSkatDialogContentProps) {
    if (loading) {
        return <LoadingBox />;
    }

    return (
        <Form skipNotifications onSubmit={onTransfer} onSubmitError={onError}>
            {({ form }) => (
                <>
                    <Dialog.Title>
                        <Trans>Transfer VAT Statement directly to Skat.dk</Trans>
                    </Dialog.Title>
                    <div className="flex mt-8">
                        <div className="w-1/2">
                            <p>
                                <Trans>
                                    You will now be transferred to skat.dk, where a VAT statement will be automatically created
                                    for the specific period. All of your data for that period, is transferred as well.
                                </Trans>
                            </p>
                            <p>
                                <Trans>
                                    You will be able to transfer your data multiple times, until you approve the statement at
                                    skat.dk. Once approved, you will not be able to transfer the data for that specific period.
                                </Trans>
                            </p>
                            <p>
                                <Trans>
                                    Read more about how to transfer your VAT statement to SKAT on{' '}
                                    <ExternalLink href="https://www.e-conomic.dk/support/artikler/overfoer-din-momsopgoerelse-til-skat">
                                        e-copedia
                                    </ExternalLink>
                                    .
                                </Trans>
                            </p>
                        </div>
                        <div className="w-1/2 ml-4">
                            <img src={PaperPlaneWithDollarSign} />
                        </div>
                    </div>
                    {errorMessage && <Alert state="error">{errorMessage}</Alert>}
                    <Dialog.Footer>
                        <Group>
                            <Button appearance="default" onClick={onClose}>
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button
                                appearance="primary"
                                autoFocus
                                type={!existingSubmission ? 'submit' : undefined}
                                dialog={
                                    existingSubmission
                                        ? props => (
                                              <AlertDialog {...props}>
                                                  <AlertDialog.Content aria-label={t`Do you want to transfer again?`}>
                                                      <AlertDialog.Title>
                                                          <Trans>Do you want to transfer again?</Trans>
                                                      </AlertDialog.Title>
                                                      <p>
                                                          <Trans>
                                                              Be aware that you have already transferred the VAT statement to SKAT
                                                              on{' '}
                                                              <span>
                                                                  {date.format(
                                                                      date.parseFromISOString(
                                                                          existingSubmission.lastUpdatedAt ??
                                                                              existingSubmission.createdAt
                                                                      )
                                                                  )}
                                                              </span>
                                                              . If you have approved the VAT statement in SKAT.dk, it can take
                                                              some time before the receipt can be accessed in the archive.
                                                          </Trans>
                                                      </p>
                                                      <AlertDialog.Footer>
                                                          <AlertDialog.Cancel>
                                                              <Button>
                                                                  <Trans>Cancel</Trans>
                                                              </Button>
                                                          </AlertDialog.Cancel>
                                                          <AlertDialog.Action>
                                                              <Button appearance="primary" onClick={form.submit} autoFocus>
                                                                  <Trans>Continue</Trans>
                                                              </Button>
                                                          </AlertDialog.Action>
                                                      </AlertDialog.Footer>
                                                  </AlertDialog.Content>
                                              </AlertDialog>
                                          )
                                        : undefined
                                }>
                                <span>
                                    <Trans>Transfer statement</Trans>
                                </span>
                                <Icon name="undock" />
                            </Button>
                        </Group>
                    </Dialog.Footer>
                </>
            )}
        </Form>
    );
}

export function TransferToSkatDialog() {
    const navigate = useNavigate();
    const agreement = useCurrentAgreement();

    const textsLoading = useLocalization(textIds);

    const location = useLocation();
    const { reportId } = (location.state as TransferToSkatDialogState) || {};

    const existingSubmissionQuery = useQuery<SkatVatStatementSubmisssionModel>(
        `reports/vat/skat-integration/statement/${reportId}`,
        null,
        { skip: !reportId }
    );

    const [errorMessage, setErrorMessage] = useState<React.ReactNode>();
    const [transferInProgress, setTransferInProgress] = useState<boolean>(false);

    async function transferToSkat() {
        setTransferInProgress(true);
        const response = await internalApiClient.post<SkatVatStatementSubmisssionModel>(
            `reports/vat/skat-integration/statement/${reportId}/transfer-to-skat`
        );
        setTransferInProgress(false);

        window.open(response.data.skatApprovalLink);
        const { Econ } = window;
        Econ.emit('transfer-to-skat.done');
        closeDialog();
    }

    function handleError(error) {
        setTransferInProgress(false);
        const message = getErrorMessage(error);
        setErrorMessage(message);
    }

    function closeDialog() {
        navigate(location.pathname);
    }

    if (location.hash !== '#transfer-to-skat' || !agreement.features.skatVatIntegration) {
        return null;
    }

    if (errorMessage && existingSubmissionQuery.data) {
        return <TransferToSkatErrorDialog errorMessage={errorMessage} onClose={closeDialog} />;
    }

    return (
        <Dialog size="sm" defaultOpen onClose={closeDialog}>
            <Dialog.Content aria-label={t`Transfer VAT Statement directly to Skat.dk`}>
                {({ close }) => (
                    <TransferToSkatDialogContent
                        loading={textsLoading || existingSubmissionQuery.loading || transferInProgress}
                        existingSubmission={existingSubmissionQuery.data}
                        errorMessage={errorMessage}
                        onTransfer={transferToSkat}
                        onError={handleError}
                        onClose={close}
                    />
                )}
            </Dialog.Content>
        </Dialog>
    );
}

const textIds = [
    'ERROR_CODE_SKATCONNECT_GENERIC_ERROR',
    'ERROR_CODE_SKATCONNECT_InvalidVatStatementInput',
    'ERROR_CODE_SKATCONNECT_VatPeriodIsNotOpen',
    'ERROR_CODE_SKATCONNECT_VatPeriodIsNotExists',
    'ERROR_CODE_SKATCONNECT_VatPeriodHasNotEnded',
    'ERROR_CODE_SKATCONNECT_VatPeriodIsMoreThanThreeYearsOld',
    'ERROR_CODE_SkatVatStatementSubmissionAlreadyApproved',
];
